import { get } from 'svelte/store';
import { format } from 'svelte-i18n';
import { gen2Request } from '../services/gen_2_http_reqs';
import { toast } from '@Shelly/shelly-design-system';
import type { Device } from '../types/Device';
import type { SettingRequest } from '../types/requests/settings/setting';
import type { CuryMode } from '../types/Cury';
import { devices } from 'src/stores/lists/devices';
import { isDiffuser } from './interface_helpers';

/**
 * Set the mode of operation for a Cury device
 *
 * This will insert predefined schedules that control the heating and operation
 * of the device suited for the appropriate room. Changing the mode from one to
 * another will remove the schedules that were created by the previously active mode.
 *
 * @param device - The Cury device
 * @param mode - The mode to set (hall, bedroom, living_room, lavatory_room, reception, workplace, or null)
 * @returns Promise with the request result
 */
export const setCuryMode = async (device: Device, mode: CuryMode): Promise<SettingRequest> => {
	const res = await gen2Request(device, 'Cury.SetMode', {
		id: device.channel,
		mode: mode,
	});

	if (res.isok) {
		const modeLabel = mode
			? get(format)(`label_${mode}`, { default: mode.replace('_', ' ') })
			: get(format)('mode_disabled', { default: 'Mode disabled' });

		toast.open({
			type: 'success',
			title: get(format)('mode_set_success', {
				default: 'Mode set to {mode}',
				values: { mode: modeLabel },
			}),
		});
	} else {
		toast.open({
			type: 'danger',
			title: get(format)('error_global', { default: 'Something went wrong!' }),
		});
	}

	return res;
};

/**
 * Set away mode for a Cury device
 *
 * Turning it on will turn off heating for both vials, turn off ambient light,
 * turn off autoswitching and disable all schedules. When away mode is active,
 * all RPC methods which can modify the device state (except this one) will
 * return an error indicating that away mode is active.
 *
 * @param device - The Cury device
 * @param on - Whether to enable or disable away mode
 * @returns Promise with the request result
 */
export const setCuryAwayMode = async (device: Device, on: boolean): Promise<SettingRequest> => {
	const res = await gen2Request(device, 'Cury.SetAwayMode', {
		id: device.channel,
		on: on,
	});

	if (res.isok) {
		toast.open({
			type: 'success',
			title: on
				? get(format)('away_mode_enabled', { default: 'Away mode enabled' })
				: get(format)('away_mode_disabled', { default: 'Away mode disabled' }),
		});
	} else {
		toast.open({
			type: 'danger',
			title: get(format)('error_global', { default: 'Something went wrong!' }),
		});
	}

	return res;
};

/**
 * Get the current mode state from device status
 *
 * @param status - Device status object
 * @param component - Component structure name (e.g., 'cury:0')
 * @returns Object with mode and away_mode state
 */
export const getCuryModeState = (status: any, component: string) => {
	return {
		mode: status?.[component]?.mode || null,
		away_mode: status?.[component]?.away_mode || false,
	};
};

export const getCuryDevicesNumber = () => {
	const allDevices = get(devices);
	return allDevices ? Object.values(allDevices).filter((d) => isDiffuser(d.type))?.length : 0;
};
